import { Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { useLang } from "@/components/LanguageContext";
import SEO from "@/components/SEO";
import ScrollReveal from "@/components/ScrollReveal";
import PageHero from "@/components/PageHero";
import LeadCTA from "@/components/LeadCTA";
import EN from "@/data/content-en";
import ZH from "@/data/content-zh";
import { PAGE_IMAGES, pathFor } from "@/data/pages";

export default function AboutPage() {
  const { lang, t } = useLang();
  const c = lang === "zh" ? ZH.about : EN.about;

  return (
    <div className={lang === "zh" ? "lang-zh" : ""}>
      <SEO pageId="about" title={c.title} description={c.metaDescription} image={PAGE_IMAGES.about} />

      <PageHero pageId="about" mainTitle={c.h1} subtitle={c.intro} image={PAGE_IMAGES.about} />

      <section id="about-body" className="bg-[var(--abs-cream)] py-24 md:py-32">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <ScrollReveal>
              <p className="text-[20px] leading-relaxed">{c.intro}</p>
            </ScrollReveal>
          </div>
          <div className="lg:col-span-7 space-y-8">
            {c.sections.map((sec, i) => (
              <ScrollReveal key={i} delay={i * 80} className="bg-white p-8 md:p-10 lift-card">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--abs-orange)] mb-4">0{i + 1}</div>
                <h3 className="serif text-2xl md:text-3xl">{sec.heading}</h3>
                <div className="mt-5 text-[15.5px] leading-relaxed text-[var(--abs-mute)] space-y-3">
                  {Array.isArray(sec.body) ? sec.body.map((p, j) => <p key={j}>{p}</p>) : <p>{sec.body}</p>}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team grid */}
      <section className="bg-white py-24" data-testid="about-team">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <ScrollReveal>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--abs-orange)] mb-4">{t.misc.ourTeam}</div>
            <h2 className="serif text-4xl md:text-6xl leading-[1.04] max-w-3xl">{c.teamHeading}</h2>
          </ScrollReveal>

          {c.teamGroupPhoto && (
            <ScrollReveal delay={100} className="mt-12">
              <div className="relative overflow-hidden">
                <img
                  src={c.teamGroupPhoto}
                  alt={lang === "zh" ? "ABS Solutions 团队合照" : "ABS Solutions team photo"}
                  className="w-full h-[260px] md:h-[420px] object-cover"
                  loading="lazy"
                  data-testid="about-team-group-photo"
                />
              </div>
            </ScrollReveal>
          )}

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.team.map((m, idx) => (
              <ScrollReveal key={m.name} delay={idx * 60} className="bg-[var(--abs-soft)] lift-card overflow-hidden flex flex-col">
                <div className="relative aspect-[3/4] bg-[var(--abs-beige)] overflow-hidden">
                  {m.photo ? (
                    <img
                      src={m.photo}
                      alt={`${m.name} — ${m.role}`}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                      loading="lazy"
                      data-testid={`about-team-photo-${idx}`}
                    />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-[var(--abs-cream)] to-[var(--abs-beige)]">
                      <div className="serif text-7xl text-[var(--abs-orange)]">{m.name.charAt(0)}</div>
                    </div>
                  )}
                </div>
                <div className="p-7">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--abs-orange)]">{m.role}</div>
                  <div className="mt-2 serif text-2xl">{m.name}</div>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-[var(--abs-mute)]">{m.bio}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--abs-ink)] text-white py-20">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 items-center gap-10">
          <div>
            <h3 className="serif text-3xl md:text-5xl leading-[1.06]">
              {lang === "zh" ? "准备好开始合作了吗？" : "Ready to start the conversation?"}
            </h3>
          </div>
          <div className="md:text-right">
            <Link to={pathFor("contact", lang)} className="abs-btn !border-white !text-white hover:!bg-white hover:!text-[var(--abs-ink)] inline-flex" data-testid="about-cta-contact">
              {t.cta.getInTouch} <ArrowRight className="h-4 w-4 arrow" />
            </Link>
          </div>
        </div>
      </section>

      <LeadCTA />
    </div>
  );
}
